/**
 * GameOver - Game Over UI Overlay
 * 
 * Features dramatic score count-up with tension/acceleration,
 * casino-inspired visual effects, and satisfying animations.
 */

import { GameStateInfo } from '../game/Game';

/** GameOver configuration */
export interface GameOverConfig {
    /** Duration of score count-up in ms (default 3000) */
    countUpDuration?: number;
    /** Callback when retry is clicked */
    onRetry?: () => void;
    /** Callback when menu is clicked */
    onMenu?: () => void;
    /** Audio callbacks */
    onHover?: () => void;
    onClick?: () => void;
    /** Called during count-up for tick sounds */
    onScoreTick?: () => void;
}

/**
 * Easing function for count-up (slow start, fast middle, slow end)
 * Creates tension at the beginning and satisfaction at the end
 */
function easeOutExpo(t: number): number {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * Alternative: Tension easing (slow-fast-slow with dramatic pause near end)
 */
function tensionEase(t: number): number {
    // Cubic bezier approximation for dramatic effect
    // Slow start, accelerate in middle, slow down dramatically at end
    if (t < 0.2) {
        // Slow start (tension building)
        return t * t * 12.5; // 0 to 0.5
    } else if (t < 0.8) {
        // Fast middle (excitement)
        const mid = (t - 0.2) / 0.6;
        return 0.5 + mid * 0.45; // 0.5 to 0.95
    } else {
        // Slow dramatic finish
        const end = (t - 0.8) / 0.2;
        return 0.95 + easeOutExpo(end) * 0.05; // 0.95 to 1.0
    }
}

/**
 * GameOver class - game over screen with score reveal
 */
export class GameOver {
    private container: HTMLDivElement;
    private config: Required<GameOverConfig>;
    
    // Element references
    private scoreEl!: HTMLElement;
    private densityEl!: HTMLElement;
    private tJunctionsEl!: HTMLElement;
    private buttonsEl!: HTMLElement;
    
    // Animation state
    private animationId: number | null = null;
    private finalScore = 0;
    private lastTickScore = 0;

    constructor(parentElement: HTMLElement, config: GameOverConfig = {}) {
        this.config = {
            countUpDuration: config.countUpDuration ?? 3000,
            onRetry: config.onRetry ?? (() => {}),
            onMenu: config.onMenu ?? (() => {}),
            onHover: config.onHover ?? (() => {}),
            onClick: config.onClick ?? (() => {}),
            onScoreTick: config.onScoreTick ?? (() => {}),
        };

        // Create container
        this.container = document.createElement('div');
        this.container.id = 'game-over';
        this.container.innerHTML = this.getHTML();
        parentElement.appendChild(this.container);

        // Apply styles
        this.applyStyles();

        // Cache elements
        this.cacheElements();

        // Setup interactions
        this.setupInteractions();

        // Initially hidden
        this.hide();
    }

    /**
     * Get the HTML structure
     */
    private getHTML(): string {
        return `
            <div class="go-background">
                <div class="go-particles"></div>
            </div>
            <div class="go-content">
                <div class="go-title">
                        <div class="go-title-line">Sort Down</div>
                        <div class="go-title-line">Clock Out</div>
                        <div class="go-title-line">Go Home</div>
                    </div>
                <div class="go-score-container">
                    <div class="go-score-label">FINAL SCORE</div>
                    <div class="go-score">0</div>
                </div>
                <div class="go-stats">
                    <div class="go-stat">
                        <span class="go-stat-value go-density">0%</span>
                        <span class="go-stat-label">DENSITY</span>
                    </div>
                    <div class="go-stat-divider"></div>
                    <div class="go-stat">
                        <span class="go-stat-value go-tjunctions">0</span>
                        <span class="go-stat-label">T-STACKS</span>
                    </div>
                </div>
                <div class="go-buttons">
                    <button class="go-btn go-btn-retry">RETRY</button>
                    <button class="go-btn go-btn-menu">MENU</button>
                </div>
            </div>
        `;
    }

    /**
     * Apply CSS styles
     */
    private applyStyles(): void {
        const style = document.createElement('style');
        style.id = 'gameover-styles';
        style.textContent = `
            #game-over {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 300;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.5s ease, visibility 0.5s ease;
            }

            #game-over.visible {
                opacity: 1;
                visibility: visible;
            }

            .go-background {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: radial-gradient(ellipse at center, rgba(20, 20, 40, 0.95) 0%, rgba(10, 10, 20, 0.98) 100%);
            }

            .go-particles {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-image: 
                    radial-gradient(2px 2px at 10% 20%, rgba(255, 215, 0, 0.4), transparent),
                    radial-gradient(2px 2px at 30% 60%, rgba(255, 215, 0, 0.3), transparent),
                    radial-gradient(1px 1px at 50% 30%, rgba(255, 255, 255, 0.5), transparent),
                    radial-gradient(2px 2px at 70% 70%, rgba(255, 215, 0, 0.4), transparent),
                    radial-gradient(1px 1px at 90% 40%, rgba(255, 255, 255, 0.4), transparent);
                background-size: 200px 200px;
                animation: shimmer 3s ease-in-out infinite;
            }

            @keyframes shimmer {
                0%, 100% { opacity: 0.5; transform: translateY(0); }
                50% { opacity: 1; transform: translateY(-5px); }
            }

            .go-content {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 20px;
            }

            .go-title {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-bottom: 30px;
                animation: titlePulse 2s ease-in-out infinite;
            }

            .go-title-line {
                font-size: clamp(28px, 8vw, 44px);
                font-weight: bold;
                color: #f44336;
                text-shadow: 0 0 20px rgba(244, 67, 54, 0.5), 0 4px 8px rgba(0, 0, 0, 0.5);
                line-height: 1.2;
            }

            @keyframes titlePulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.02); }
            }

            .go-score-container {
                text-align: center;
                margin-bottom: 30px;
            }

            .go-score-label {
                font-size: 14px;
                color: #888;
                letter-spacing: 3px;
                margin-bottom: 10px;
            }

            .go-score {
                font-size: clamp(48px, 15vw, 80px);
                font-weight: bold;
                color: #FFD700;
                text-shadow: 0 0 30px rgba(255, 215, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.5);
                font-variant-numeric: tabular-nums;
            }

            .go-score.counting {
                animation: scoreShake 0.1s ease-in-out infinite;
            }

            @keyframes scoreShake {
                0%, 100% { transform: translateX(0); }
                25% { transform: translateX(-2px); }
                75% { transform: translateX(2px); }
            }

            .go-score.final {
                animation: scoreFinal 0.5s ease-out;
            }

            @keyframes scoreFinal {
                0% { transform: scale(1.2); }
                50% { transform: scale(0.95); }
                100% { transform: scale(1); }
            }

            .go-stats {
                display: flex;
                align-items: center;
                gap: 30px;
                margin-bottom: 40px;
            }

            .go-stat {
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            .go-stat-value {
                font-size: 28px;
                font-weight: bold;
                color: #fff;
            }

            .go-stat-label {
                font-size: 12px;
                color: #666;
                letter-spacing: 2px;
                margin-top: 5px;
            }

            .go-stat-divider {
                width: 2px;
                height: 40px;
                background: linear-gradient(transparent, #444, transparent);
            }

            .go-tjunctions .go-stat-value {
                color: #FFD700;
            }

            .go-buttons {
                display: flex;
                gap: 20px;
                opacity: 0;
                transform: translateY(20px);
                transition: opacity 0.5s ease, transform 0.5s ease;
            }

            .go-buttons.visible {
                opacity: 1;
                transform: translateY(0);
            }

            .go-btn {
                padding: 15px 40px;
                font-size: 18px;
                font-weight: bold;
                font-family: inherit;
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: transform 0.15s ease, box-shadow 0.15s ease;
                position: relative;
                overflow: hidden;
            }

            .go-btn::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                transition: left 0.5s ease;
            }

            .go-btn:hover::before {
                left: 100%;
            }

            .go-btn:hover {
                transform: scale(1.05);
            }

            .go-btn:active {
                transform: scale(0.98);
            }

            .go-btn-retry {
                background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
                color: white;
                box-shadow: 0 4px 15px rgba(76, 175, 80, 0.4);
            }

            .go-btn-retry:hover {
                box-shadow: 0 6px 25px rgba(76, 175, 80, 0.6);
            }

            .go-btn-menu {
                background: linear-gradient(135deg, #666 0%, #555 100%);
                color: white;
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }

            .go-btn-menu:hover {
                box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
            }

            /* High score highlight */
            .go-score.highscore {
                color: #00ff88;
                text-shadow: 0 0 40px rgba(0, 255, 136, 0.8), 0 4px 12px rgba(0, 0, 0, 0.5);
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Cache element references
     */
    private cacheElements(): void {
        this.scoreEl = this.container.querySelector('.go-score')!;
        this.densityEl = this.container.querySelector('.go-density')!;
        this.tJunctionsEl = this.container.querySelector('.go-tjunctions')!;
        this.buttonsEl = this.container.querySelector('.go-buttons')!;
    }

    /**
     * Setup button interactions
     */
    private setupInteractions(): void {
        const retryBtn = this.container.querySelector('.go-btn-retry') as HTMLButtonElement;
        const menuBtn = this.container.querySelector('.go-btn-menu') as HTMLButtonElement;

        retryBtn.addEventListener('click', () => {
            this.config.onClick();
            this.hide();
            this.config.onRetry();
        });

        menuBtn.addEventListener('click', () => {
            this.config.onClick();
            this.hide();
            this.config.onMenu();
        });

        retryBtn.addEventListener('mouseenter', () => this.config.onHover());
        menuBtn.addEventListener('mouseenter', () => this.config.onHover());
    }

    /**
     * Show the game over screen with score count-up
     */
    show(state: GameStateInfo): void {
        // Cancel any existing animation
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
        }

        this.finalScore = Math.floor(state.score);
        this.lastTickScore = 0;

        // Set static values immediately
        this.densityEl.textContent = `${state.density.toFixed(1)}%`;
        this.tJunctionsEl.textContent = state.tJunctionCount.toString();

        // Reset score display
        this.scoreEl.textContent = '0';
        this.scoreEl.classList.remove('final', 'highscore');
        this.scoreEl.classList.add('counting');

        // Hide buttons initially
        this.buttonsEl.classList.remove('visible');

        // Show container
        this.container.classList.add('visible');

        // Start count-up animation after a brief delay
        setTimeout(() => {
            this.startCountUp();
        }, 500);
    }

    /**
     * Start the score count-up animation
     */
    private startCountUp(): void {
        const startTime = performance.now();
        const duration = this.config.countUpDuration;
        
        // Determine tick interval based on score magnitude
        const tickInterval = this.finalScore > 10000 ? 500 : 
                            this.finalScore > 1000 ? 100 : 50;

        const animate = (currentTime: number): void => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Use tension easing for dramatic effect
            const easedProgress = tensionEase(progress);
            const currentScore = Math.floor(easedProgress * this.finalScore);
            
            // Update display
            this.scoreEl.textContent = currentScore.toLocaleString();
            
            // Tick sound at intervals
            if (currentScore - this.lastTickScore >= tickInterval) {
                this.config.onScoreTick();
                this.lastTickScore = currentScore;
            }
            
            if (progress < 1) {
                this.animationId = requestAnimationFrame(animate);
            } else {
                // Animation complete
                this.scoreEl.textContent = this.finalScore.toLocaleString();
                this.scoreEl.classList.remove('counting');
                this.scoreEl.classList.add('final');
                
                // Show buttons
                setTimeout(() => {
                    this.buttonsEl.classList.add('visible');
                }, 300);
                
                this.animationId = null;
            }
        };

        this.animationId = requestAnimationFrame(animate);
    }

    /**
     * Hide the game over screen
     */
    hide(): void {
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        this.container.classList.remove('visible');
    }

    /**
     * Check if visible
     */
    isVisible(): boolean {
        return this.container.classList.contains('visible');
    }

    /**
     * Update callbacks
     */
    setCallbacks(config: Partial<GameOverConfig>): void {
        if (config.onRetry) this.config.onRetry = config.onRetry;
        if (config.onMenu) this.config.onMenu = config.onMenu;
        if (config.onHover) this.config.onHover = config.onHover;
        if (config.onClick) this.config.onClick = config.onClick;
        if (config.onScoreTick) this.config.onScoreTick = config.onScoreTick;
    }

    /**
     * Clean up
     */
    destroy(): void {
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
        }
        this.container.remove();
        document.getElementById('gameover-styles')?.remove();
    }
}
